import { useContext, useEffect, type ReactNode } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { reflectKeys } from '~/lib/query-keys';
import { CaretRight } from '@phosphor-icons/react';
import { useDayOneState } from '~/hooks/queries/use-day-one';
import { useReflectProposalsAll } from '~/hooks/queries/use-reflect';
import { ChatQuestionsDockedContext } from './chat-questions-docked-context';
import { deriveRecapDecision } from './onboarding-recap-rows';
import { OnboardingRecapCard } from './onboarding-recap-card';
import { StarterCronProposalCard } from './starter-cron-proposal-card';
import { QuickRepliesCard } from './quick-replies-card';
import {
  SIGNAL_PART_TYPE,
  type OnboardingRecapSignalData,
  type QuickRepliesSignalData,
  type StarterCronProposalSignalData,
} from './tool-signals';

/**
 * FRONT-3223 (eddy, 2026-10-28): the cards that ask for a decision — the
 * starter automations and the "here's what I'll remember" recap — are decided
 * inside the reply box, like the open questions, and the conversation keeps
 * each as one collapsed line. Only where the box answers questions
 * (`ChatQuestionsDockedContext`); elsewhere they are the cards they were.
 */

const STARTER = SIGNAL_PART_TYPE('starter-cron-proposal');
const RECAP = SIGNAL_PART_TYPE('onboarding-recap');
const QUICK_REPLIES = SIGNAL_PART_TYPE('quick-replies');

type MessageLike = { parts?: ReadonlyArray<{ type: string; data?: unknown }> };

export type DockableSignal =
  | { kind: 'starter'; data: StarterCronProposalSignalData; at: number }
  | { kind: 'recap'; data: OnboardingRecapSignalData; at: number };

/** Pure — the newest automations and recap signals in the conversation, with their position. */
export function findDockableSignals(messages: ReadonlyArray<MessageLike>): {
  starter?: Extract<DockableSignal, { kind: 'starter' }>;
  recap?: Extract<DockableSignal, { kind: 'recap' }>;
} {
  const found: { starter?: Extract<DockableSignal, { kind: 'starter' }>; recap?: Extract<DockableSignal, { kind: 'recap' }> } = {};
  let at = 0;
  for (const m of messages) {
    for (const p of m.parts ?? []) {
      at++;
      if (p.type === STARTER && p.data) found.starter = { kind: 'starter', data: p.data as StarterCronProposalSignalData, at };
      if (p.type === RECAP && p.data) found.recap = { kind: 'recap', data: p.data as OnboardingRecapSignalData, at };
    }
  }
  return found;
}

/**
 * Pure — the quick replies ("Let's chat" / "Let's talk") on the LAST message,
 * the only ones the conversation ever offers (`QuickRepliesCard`), or null.
 */
export function findLastQuickReplies(messages: ReadonlyArray<MessageLike>): QuickRepliesSignalData | null {
  const last = messages[messages.length - 1];
  const part = last?.parts?.find((p) => p.type === QUICK_REPLIES && p.data);
  const data = part?.data as QuickRepliesSignalData | undefined;
  return data?.options?.length ? data : null;
}

/**
 * Pure — which card goes in the box: of the undecided ones, the one met last
 * in the conversation (the newest ask), or none.
 */
export function pickDockedCard(
  found: ReturnType<typeof findDockableSignals>,
  undecided: { starter: boolean; recap: boolean },
): DockableSignal | null {
  const open = [
    found.starter && undecided.starter ? found.starter : null,
    found.recap && undecided.recap ? found.recap : null,
  ].filter((s): s is DockableSignal => s !== null);
  return open.sort((a, b) => b.at - a.at)[0] ?? null;
}

/**
 * The card to decide in the box, as `ChatInput`'s `topSlot`, or undefined.
 * The card itself renders there in full — the same component, so deciding it
 * in the box is deciding it.
 */
export function useChatCardDock({
  messages,
  enabled,
  onSendMessage,
  onStartCall,
}: {
  messages: ReadonlyArray<MessageLike>;
  enabled: boolean;
  /** For the quick replies: "Let's chat" sends its text, "Let's talk" starts the call. */
  onSendMessage?: (text: string) => void | Promise<void>;
  onStartCall?: () => void;
}): { topSlot?: () => ReactNode } {
  const found = enabled ? findDockableSignals(messages) : {};
  const { data: dayOne } = useDayOneState(enabled && !!found.starter);
  const { data: proposals } = useReflectProposalsAll();
  // A recap staged after the list was read is only known from the
  // conversation (the onboarding page has no live-update stream), so a new
  // recap run re-reads the list — same reason as the question dock.
  const qc = useQueryClient();
  const recapRunId = found.recap?.data.runId;
  useEffect(() => {
    if (recapRunId !== undefined) void qc.invalidateQueries({ queryKey: reflectKeys.proposals() });
  }, [recapRunId, qc]);
  const card = pickDockedCard(found, {
    starter: !!dayOne && (dayOne.proposalStatus ?? 'pending') === 'pending',
    recap: !!proposals && !!found.recap && deriveRecapDecision(proposals, found.recap.data.runId) === null,
  });
  // FRONT-3223 (eddy, 2026-10-28): the last message's quick replies sit in
  // the box too, once no card is waiting there.
  const quick = enabled && !card ? findLastQuickReplies(messages) : null;
  if (quick) {
    return {
      topSlot: () => (
        <div data-testid="chat-quick-replies-dock" className="p-2 [&>div]:max-w-none">
          <QuickRepliesCard options={quick.options} onSendMessage={onSendMessage} onStartCall={onStartCall} isLastMessage />
        </div>
      ),
    };
  }
  if (!card) return {};
  return {
    topSlot: () => (
      <div data-testid="chat-card-dock" data-kind={card.kind} className="p-2 [&>div]:max-w-none [&>div]:border-0">
        <ChatQuestionsDockedContext.Provider value={false}>
          {card.kind === 'starter' ? (
            <StarterCronProposalCard crons={card.data.crons} />
          ) : (
            <OnboardingRecapCard runId={card.data.runId} items={card.data.items} />
          )}
        </ChatQuestionsDockedContext.Provider>
      </div>
    ),
  };
}

/**
 * In the conversation, where the box decides these cards: one collapsed line
 * that opens to the full card — the Builder's record pattern. A native
 * <details>, so it needs no state.
 */
export function DockableCardRecord({ label, children }: { label: string; children: ReactNode }) {
  const docked = useContext(ChatQuestionsDockedContext);
  if (!docked) return <>{children}</>;
  return (
    <details data-testid="chat-card-record" className="group/cr max-w-[85%] rounded-lg border border-border bg-card">
      <summary className="flex cursor-pointer list-none items-center gap-2 rounded-lg px-3 py-2 outline-none focus-visible:ring-2 focus-visible:ring-ring [&::-webkit-details-marker]:hidden">
        <CaretRight
          size={11}
          weight="bold"
          aria-hidden
          className="shrink-0 text-muted-foreground transition-transform group-open/cr:rotate-90 motion-reduce:transition-none"
        />
        <span className="text-xs font-medium">{label}</span>
      </summary>
      <div className="border-t border-border [&>div]:max-w-none [&>div]:rounded-none [&>div]:border-0">{children}</div>
    </details>
  );
}

/** The conversation's quick replies, left out where the box offers them instead. */
export function DockableQuickReplies({ children }: { children: ReactNode }) {
  return useContext(ChatQuestionsDockedContext) ? null : <>{children}</>;
}
